// MoroccoRegionsMap — interactive map showing the 3 Xaluca regions across
// Morocco. Each region is rendered as:
//   • a translucent circle highlighting the geographical footprint
//   • a central labelled pin
//   • small property pins for every property assigned to the region
//
// Clicking a region (on the map or in the side list) selects it and reveals
// the property list. On mobile we collapse the map into an accordion of
// stacked region cards while preserving the same click→reveal interaction.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapContainer, TileLayer, Marker, Popup, Circle, ZoomControl, useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapTileToggle from "@/components/maps/MapTileToggle";
import { ChevronRight, MapPin, Building, Tent, Mountain } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { getHotelLocation } from "@/lib/hotelLocations";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const MOROCCO_CENTER = [31.5, -6.0];
const MOROCCO_ZOOM = 6;
// Tight bounds so the map can't pan off the country.
const MOROCCO_BOUNDS = [
  [27.5, -10.5],
  [36.0, -1.0],
];

const CATEGORY_ICON = {
  hotel: Building,
  bivouac: Tent,
  refuge: Mountain,
};

// Big numbered region marker. White ring + brand-colored fill + number.
const regionIcon = (color, number, active) =>
  L.divIcon({
    className: "xaluca-region-marker",
    html: `
      <div style="position:relative;display:flex;align-items:center;justify-content:center;">
        <span style="
          width:${active ? 56 : 46}px;height:${active ? 56 : 46}px;border-radius:50%;
          background:${color};
          box-shadow:${active
            ? `0 8px 22px ${color}66, 0 0 0 6px rgba(253,251,247,0.95), 0 0 0 9px ${color}44`
            : "0 4px 12px rgba(28,22,18,0.4), 0 0 0 5px rgba(253,251,247,0.95)"};
          display:flex;align-items:center;justify-content:center;
          color:#FDFBF7;font-weight:600;font-size:${active ? 18 : 16}px;font-family:'Outfit',sans-serif;
          transition: all 0.2s ease;
        ">${number}</span>
      </div>
    `,
    iconSize: [active ? 56 : 46, active ? 56 : 46],
    iconAnchor: [active ? 28 : 23, active ? 28 : 23],
    popupAnchor: [0, -28],
  });

// Small property pin — used to mark each property inside a region.
const propertyIcon = (color) =>
  L.divIcon({
    className: "xaluca-property-marker",
    html: `
      <div style="
        width:14px;height:14px;border-radius:50%;
        background:${color};
        box-shadow:0 2px 6px rgba(28,22,18,0.35), 0 0 0 3px rgba(253,251,247,0.95);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

// Pan + zoom to the selected region.
const MapFocus = ({ region }) => {
  const map = useMap();
  useEffect(() => {
    if (!region) {
      map.flyTo(MOROCCO_CENTER, MOROCCO_ZOOM, { duration: 0.9 });
      return;
    }
    map.flyTo(region.center, 8, { duration: 0.9 });
  }, [region, map]);
  return null;
};

const RegionListItem = ({ region, index, hotelsBySlug, directionsBySlug, active, onSelect, lang }) => {
  const props = (region.property_slugs || [])
    .map((s) => hotelsBySlug[s])
    .filter(Boolean);
  return (
    <li
      className={`border ${active ? "border-[#C16542]" : "border-[#2C2621]/10"} bg-white transition-colors`}
      data-testid={`region-card-${region.slug}`}
    >
      <button
        type="button"
        onClick={() => onSelect(region.slug)}
        className={`w-full text-left px-5 md:px-7 py-5 md:py-6 flex items-start gap-4 transition-colors ${
          active ? "bg-[#FBF6EE]" : "hover:bg-[#FBF6EE]"
        }`}
        aria-expanded={active}
        aria-controls={`region-${region.slug}-properties`}
        data-testid={`region-card-toggle-${region.slug}`}
      >
        {/* Numbered marker */}
        <span
          className="shrink-0 mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-full text-[#FDFBF7] font-medium text-base"
          style={{ background: region.color }}
        >
          {index + 1}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3">
            <h3
              className="font-serif-x text-xl md:text-2xl tracking-tight text-[#1A1513]"
              data-testid={`region-card-name-${region.slug}`}
            >
              {pick(region.name, lang)}
            </h3>
            <span
              className="text-[10px] tracking-[0.25em] uppercase whitespace-nowrap"
              style={{ color: region.color }}
              data-testid={`region-card-count-${region.slug}`}
            >
              {props.length}{" "}
              {pick(
                props.length === 1
                  ? { en: "property", fr: "propriété", es: "propiedad" }
                  : { en: "properties", fr: "propriétés", es: "propiedades" },
                lang
              )}
            </span>
          </div>
          <p className="mt-2 text-sm text-[#5C5248] leading-relaxed">
            {pick(region.description, lang)}
          </p>
        </div>

        <ChevronRight
          className={`hidden md:block w-4 h-4 mt-1 transition-transform ${active ? "rotate-90 text-[#C16542]" : "text-[#5C5248]"}`}
          strokeWidth={1.8}
        />
      </button>

      {/* Property list — visible when active */}
      {active ? (
        <div
          id={`region-${region.slug}-properties`}
          className="px-5 md:px-7 pb-5 md:pb-7 pt-1"
          data-testid={`region-properties-${region.slug}`}
        >
          {props.length === 0 ? (
            <p className="text-xs text-[#5C5248] italic">
              {pick(
                { en: "No properties assigned yet.", fr: "Aucune propriété assignée.", es: "Sin propiedades asignadas." },
                lang
              )}
            </p>
          ) : (
            <ul className="space-y-2">
              {props.map((h) => {
                const Icon = CATEGORY_ICON[h.category] || MapPin;
                const dir = directionsBySlug[h.slug] || {};
                // Thumbnail priority: CMS override → static HOTELS image.
                const thumb = (dir.thumbnail_url && dir.thumbnail_url.trim()) || h.image || "";
                return (
                  <li key={h.slug}>
                    <Link
                      to={`/hotels/${h.slug}`}
                      className="group flex items-stretch gap-3 border border-[#2C2621]/10 hover:border-[#C16542] hover:bg-[#FBF6EE] transition-colors overflow-hidden"
                      data-testid={`region-property-link-${region.slug}-${h.slug}`}
                    >
                      {/* Thumbnail */}
                      <span className="relative w-20 sm:w-24 aspect-[4/3] shrink-0 overflow-hidden bg-[#F3EBDD]">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={h.name}
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                          />
                        ) : (
                          <span
                            className="absolute inset-0 flex items-center justify-center text-[#FDFBF7]"
                            style={{ background: region.color }}
                          >
                            <Icon className="w-4 h-4" strokeWidth={1.8} />
                          </span>
                        )}
                      </span>

                      {/* Text */}
                      <span className="flex-1 min-w-0 py-2.5 pr-3 flex items-center">
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-[#1A1513] font-medium truncate group-hover:text-[#C16542] transition-colors">
                            {h.name}
                          </span>
                          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#5C5248] mt-0.5 truncate">
                            {h.location}
                          </span>
                        </span>
                        <ChevronRight
                          className="w-3.5 h-3.5 text-[#5C5248] group-hover:text-[#C16542] shrink-0 transition-colors"
                          strokeWidth={1.8}
                        />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </li>
  );
};

const MoroccoRegionsMap = ({ regions = [], directionsBySlug = {} }) => {
  const { lang } = useLanguage();
  const [selected, setSelected] = useState(null);

  const hotelsBySlug = useMemo(
    () => Object.fromEntries(HOTELS.map((h) => [h.slug, h])),
    []
  );

  const sortedRegions = useMemo(
    () => [...regions].sort((a, b) => (a.order || 99) - (b.order || 99)),
    [regions]
  );

  const selectedRegion = useMemo(
    () => sortedRegions.find((r) => r.slug === selected),
    [sortedRegions, selected]
  );

  if (sortedRegions.length === 0) return null;

  return (
    <section
      className="bg-[#FBF6EE] border-y border-[#2C2621]/10"
      data-testid="morocco-regions-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <header className="mb-10 md:mb-12 max-w-3xl">
          <p className="overline text-[#C16542]">
            {pick(
              { en: "Geographical footprint", fr: "Empreinte géographique", es: "Huella geográfica" },
              lang
            )}
          </p>
          <h2
            className="font-serif-x text-3xl sm:text-4xl lg:text-5xl mt-3 tracking-tight leading-[1.05] text-[#1A1513]"
            data-testid="morocco-regions-title"
          >
            {pick(
              { en: "Xaluca Across Morocco", fr: "Xaluca à travers le Maroc", es: "Xaluca a través de Marruecos" },
              lang
            )}
          </h2>
          <p className="mt-4 text-base text-[#5C5248] leading-relaxed">
            {pick(
              {
                en: "Discover our hotels and desert camps across the country's most remarkable regions.",
                fr: "Découvrez nos hôtels et bivouacs dans les régions les plus remarquables du pays.",
                es: "Descubra nuestros hoteles y campamentos en las regiones más extraordinarias del país.",
              },
              lang
            )}
          </p>
        </header>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-start">
          {/* Map — hidden on mobile, replaced by stacked accordion below */}
          <div
            className="hidden lg:block relative h-[560px] border border-[#2C2621]/10 bg-[#F3EBDD] overflow-hidden"
            data-testid="morocco-regions-map"
          >
            <MapContainer
              center={MOROCCO_CENTER}
              zoom={MOROCCO_ZOOM}
              minZoom={5}
              maxZoom={11}
              maxBounds={MOROCCO_BOUNDS}
              maxBoundsViscosity={0.85}
              scrollWheelZoom={false}
              zoomControl={false}
              attributionControl={false}
              style={{ height: "100%", width: "100%", background: "#F3EBDD" }}
            >
              <MapTileToggle
                mapUrl="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                mapAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                mapSubdomains={['a','b','c','d']}
                mapMaxZoom={18}
              />
              <ZoomControl position="topright" />
              <MapFocus region={selectedRegion} />

              {sortedRegions.map((region, idx) => {
                const active = region.slug === selected;
                const props = (region.property_slugs || [])
                  .map((s) => hotelsBySlug[s])
                  .filter(Boolean);
                return (
                  <React.Fragment key={region.slug}>
                    {/* Region highlight circle */}
                    <Circle
                      center={region.center}
                      radius={(region.radius_km || 80) * 1000}
                      pathOptions={{
                        color: region.color,
                        weight: active ? 2.5 : 1.5,
                        fillColor: region.color,
                        fillOpacity: active ? 0.22 : 0.12,
                      }}
                      eventHandlers={{
                        click: () => setSelected(region.slug),
                      }}
                    />

                    {/* Numbered region pin */}
                    <Marker
                      position={region.center}
                      icon={regionIcon(region.color, idx + 1, active)}
                      eventHandlers={{
                        click: () => setSelected(region.slug),
                      }}
                    >
                      <Popup closeButton={false}>
                        <div className="text-[#1A1513]" style={{ minWidth: 180 }}>
                          <p className="text-[9px] tracking-[0.25em] uppercase" style={{ color: region.color }}>
                            {pick(
                              { en: "Region", fr: "Région", es: "Región" },
                              lang
                            )}
                          </p>
                          <p className="font-serif-x text-lg leading-tight mt-1">
                            {pick(region.name, lang)}
                          </p>
                          <p className="text-xs text-[#5C5248] mt-1">
                            {props.length}{" "}
                            {pick(
                              props.length === 1
                                ? { en: "property", fr: "propriété", es: "propiedad" }
                                : { en: "properties", fr: "propriétés", es: "propiedades" },
                              lang
                            )}
                          </p>
                        </div>
                      </Popup>
                    </Marker>

                    {/* Property pins — only render when region is active */}
                    {active
                      ? props.map((h) => {
                          // Pull GPS coords from the static hotelLocations registry.
                          const loc = getHotelLocation(h.slug, h);
                          const [lat, lng] = loc.coords || region.center;
                          return (
                            <Marker
                              key={`${region.slug}-${h.slug}`}
                              position={[lat, lng]}
                              icon={propertyIcon(region.color)}
                            >
                              <Popup closeButton={false}>
                                <div style={{ minWidth: 160 }}>
                                  <p className="text-[9px] tracking-[0.25em] uppercase text-[#C16542]">
                                    {h.location}
                                  </p>
                                  <p className="font-serif-x text-base mt-1 leading-tight">
                                    {h.name}
                                  </p>
                                  <Link
                                    to={`/hotels/${h.slug}`}
                                    className="inline-flex items-center gap-1 mt-2 text-[10px] tracking-[0.2em] uppercase text-[#C16542] hover:gap-1.5 transition-all"
                                  >
                                    {pick(
                                      { en: "View", fr: "Voir", es: "Ver" },
                                      lang
                                    )}
                                    <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
                                  </Link>
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })
                      : null}
                  </React.Fragment>
                );
              })}
            </MapContainer>

            {/* Reset button — only when a region is selected */}
            {selected ? (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="absolute top-4 left-4 z-[400] bg-[#2C2621] text-[#FDFBF7] px-3 py-2 text-[10px] tracking-[0.25em] uppercase hover:bg-[#1A1513] transition-colors"
                data-testid="morocco-regions-reset"
              >
                {pick(
                  { en: "Show all regions", fr: "Voir toutes les régions", es: "Ver todas las regiones" },
                  lang
                )}
              </button>
            ) : null}
          </div>

          {/* Region list — also acts as the mobile accordion */}
          <ul
            className="space-y-3 lg:max-h-[560px] lg:overflow-y-auto lg:pr-2"
            data-testid="morocco-regions-list"
          >
            {sortedRegions.map((region, idx) => (
              <RegionListItem
                key={region.slug}
                region={region}
                index={idx}
                hotelsBySlug={hotelsBySlug}
                directionsBySlug={directionsBySlug}
                active={region.slug === selected}
                onSelect={(slug) =>
                  setSelected((cur) => (cur === slug ? null : slug))
                }
                lang={lang}
              />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default MoroccoRegionsMap;
